import React, { useMemo, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Dismiss16Regular, PersonAdd20Regular, Delete16Regular } from "@fluentui/react-icons";
import { chatService, type ChatConversation, type ChatMember } from "../../services/chatService";
import type { AppUser } from "../../services/userService";
import { InitialsAvatar, MiniSpinner } from "./chatUi";
import { AUB, INK, INK2, LINE, BG_SOFT, PLUM, gruppenName } from "./chatTheme";

type Props = {
  conversation: ChatConversation;
  members: ChatMember[];
  users: AppUser[];
  currentUserId: string | null;
  /** Anzeigename zu einem `userId`/E-Mail/Kürzel — kommt aus dem Thread. */
  nameOf: (key: string) => string;
  /** Mitglieder pflegen (Admin/GFGL/Teamleiter) — vom Server geliefert. */
  canManage: boolean;
  /** Gruppe löschen (Admin/GFGL) — vom Server geliefert. */
  canDelete: boolean;
  onClose: () => void;
  /** Nach jeder Mitgliederänderung: Detail + Liste neu laden. */
  onChanged: () => void;
  /** Nach dem Löschen der Gruppe. */
  onDeleted: () => void;
};

/** Ist `u` (App-Nutzer) bereits Mitglied? `chat_member.userId` trägt historisch
 *  auch E-Mail/Username statt der id — daher der Abgleich über alle drei. */
const istMitglied = (u: AppUser, members: ChatMember[]): boolean => {
  const keys = new Set(
    [u.id, u.email ?? "", u.username ?? ""].map((k) => k.trim().toLowerCase()).filter(Boolean),
  );
  return members.some((m) => keys.has(m.userId.trim().toLowerCase()));
};

/**
 * Mitglieder einer Chat-Gruppe: anzeigen, hinzufügen, entfernen — und für
 * Admin/GFGL die Gruppe löschen. Öffnet sich über die Kopfleiste des Team-Chats.
 *
 * Die Mitgliedschaft ist bewusst NUR die des Chats (`chat_member`): am `AppTeam`
 * hängen Kontaktverantwortlichkeit (BO), Team-Sichtbarkeiten und Abwesenheiten —
 * wer hier jemanden in eine Gruppe holt, soll ihm keine Datenrechte geben.
 */
export const GroupMembersDialog: React.FC<Props> = ({
  conversation,
  members,
  users,
  currentUserId,
  nameOf,
  canManage,
  canDelete,
  onClose,
  onChanged,
  onDeleted,
}) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [loeschenBestaetigt, setLoeschenBestaetigt] = useState(false);
  const titel = gruppenName(conversation.title) || "Gruppe";

  const sortierteMitglieder = useMemo(
    () =>
      members
        .slice()
        .sort((a, b) => nameOf(a.userId).localeCompare(nameOf(b.userId), "de")),
    [members, nameOf],
  );

  const kandidaten = useMemo(() => {
    const suche = q.trim().toLowerCase();
    return users
      .filter((u) => !istMitglied(u, members))
      .filter((u) => (!suche ? true : nameOf(u.id).toLowerCase().includes(suche)))
      .sort((a, b) => nameOf(a.id).localeCompare(nameOf(b.id), "de"))
      .slice(0, 30);
  }, [users, members, q, nameOf]);

  const mitFehler = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setFehler(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const hinzufuegen = (userId: string) =>
    mitFehler(`add:${userId}`, () => chatService.addGroupMember(conversation.id, userId));

  const entfernen = (userId: string) =>
    mitFehler(`del:${userId}`, () => chatService.removeGroupMember(conversation.id, userId));

  const gruppeLoeschen = () =>
    mitFehler("group", async () => {
      await chatService.deleteGroup(conversation.id);
      onDeleted();
    });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Mitglieder der Gruppe"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        // Über dem Fenster-Layer (WindowLayer 9000) — sonst verschwindet der
        // Dialog, sobald der Chat in einem Fenster geöffnet wird.
        zIndex: 100000,
        background: "rgba(20,8,20,0.34)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        className="kvp-rd"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(460px, 100%)",
          maxHeight: "min(680px, 88vh)",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          border: `1px solid ${LINE}`,
          borderRadius: 14,
          boxShadow: "0 24px 64px rgba(20,8,20,0.28)",
          fontFamily: "'Barlow', 'Segoe UI', sans-serif",
          overflow: "hidden",
        }}
      >
        {/* Kopf */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: `1px solid ${LINE}` }}>
          <InitialsAvatar name={titel} size={36} square bg={PLUM} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 800, color: INK, fontSize: 16, fontFamily: "'Playfair Display', Georgia, serif", overflowWrap: "anywhere" }}>
              {titel}
            </div>
            <div style={{ color: INK2, fontSize: 12 }}>{members.length} Mitglieder</div>
          </div>
          <button type="button" aria-label="Schließen" onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: INK2, display: "inline-flex" }}>
            <Dismiss16Regular />
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "8px 8px 12px" }}>
          {fehler && (
            <div role="alert" style={{ margin: "6px 8px", padding: "6px 10px", borderRadius: 8, background: "#fdf0f0", color: "#8a2626", fontSize: 12.5 }}>
              {fehler}
            </div>
          )}

          {/* Mitglieder */}
          {sortierteMitglieder.length === 0 ? (
            <div style={{ padding: "12px 10px", color: INK2, fontSize: 13, fontStyle: "italic" }}>
              Diese Gruppe hat noch keine Mitglieder.
            </div>
          ) : (
            sortierteMitglieder.map((m) => (
              <div key={m.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 10 }}>
                <InitialsAvatar name={nameOf(m.userId)} userId={m.userId} size={32} />
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13.5, color: INK }}>
                  {nameOf(m.userId)}
                  {m.userId === currentUserId && <span style={{ color: INK2 }}> (du)</span>}
                </span>
                {canManage && (
                  <button
                    type="button"
                    aria-label={`${nameOf(m.userId)} entfernen`}
                    title="Aus der Gruppe entfernen"
                    disabled={busy != null}
                    onClick={() => entfernen(m.userId)}
                    style={{ border: `1px solid ${LINE}`, background: "#fff", borderRadius: 8, width: 30, height: 30, cursor: busy ? "default" : "pointer", color: INK2, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  >
                    {busy === `del:${m.userId}` ? <MiniSpinner /> : <Dismiss16Regular />}
                  </button>
                )}
              </div>
            ))
          )}

          {/* Hinzufügen */}
          {canManage && (
            <div style={{ marginTop: 10, borderTop: `1px solid ${LINE}`, paddingTop: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 8px", color: AUB, fontSize: 12.5, fontWeight: 700 }}>
                <PersonAdd20Regular />
                Person hinzufügen
              </div>
              <input
                type="text"
                value={q}
                autoFocus
                placeholder="Name suchen…"
                onChange={(e) => setQ(e.target.value)}
                style={{ width: "calc(100% - 16px)", boxSizing: "border-box", margin: "0 8px 8px", padding: "9px 12px", border: `1px solid ${LINE}`, borderRadius: 10, background: BG_SOFT, fontFamily: "inherit", fontSize: 13.5 }}
              />
              {kandidaten.length === 0 ? (
                <div style={{ padding: "4px 10px", color: INK2, fontSize: 12.5 }}>
                  {q.trim() ? "Keine passende Person." : "Alle Personen sind bereits Mitglied."}
                </div>
              ) : (
                kandidaten.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    disabled={busy != null}
                    onClick={() => hinzufuegen(u.id)}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", border: "none", background: "transparent", padding: "6px 8px", borderRadius: 10, cursor: busy ? "default" : "pointer" }}
                  >
                    <InitialsAvatar name={nameOf(u.id)} userId={u.id} size={30} />
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13.5, color: INK }}>
                      {nameOf(u.id)}
                    </span>
                    {busy === `add:${u.id}` && <MiniSpinner />}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Fuß: Gruppe löschen */}
        {canDelete && (
          <div style={{ borderTop: `1px solid ${LINE}`, padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
            {!loeschenBestaetigt ? (
              <button
                type="button"
                onClick={() => setLoeschenBestaetigt(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${LINE}`, background: "#fff", color: "#8a2626", borderRadius: 10, padding: "7px 12px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
              >
                <Delete16Regular />
                Gruppe löschen
              </button>
            ) : (
              <>
                <span style={{ fontSize: 12.5, color: INK2 }}>
                  Gruppe wirklich löschen? Der Verlauf bleibt erhalten.
                </span>
                <span style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button type="button" onClick={() => setLoeschenBestaetigt(false)} style={{ border: `1px solid ${LINE}`, background: "#fff", borderRadius: 10, padding: "7px 12px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", color: INK }}>
                    Abbrechen
                  </button>
                  <button type="button" disabled={busy != null} onClick={gruppeLoeschen} style={{ border: "none", background: "#8a2626", color: "#fff", borderRadius: 10, padding: "7px 12px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                    {busy === "group" ? "Löscht…" : "Löschen"}
                  </button>
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default GroupMembersDialog;
